import { lazy, Suspense } from "react";
import { createBrowserRouter, Outlet } from "react-router-dom";
import { Providers } from "@/providers";
import { ApplicationLoadError, LoadingPage } from "@evolys/ui";
import { PortalCapabilityRoute } from "@evolys/auth";
import ErrorPage from "@/pages/ErrorPage";

const PortalLayout = lazy(() => import("@/layouts/PortalLayout"));
const AuthLayout = lazy(() => import("@/layouts/AuthLayout"));

const DashboardPage = lazy(() => import("@/pages/DashboardPage"));
const OrdersPage = lazy(() => import("@/pages/orders/OrdersPage"));
const NewOrderPage = lazy(() => import("@/pages/orders/NewOrderPage"));
const OrderDetailPage = lazy(() => import("@/pages/orders/OrderDetailPage"));
const ResultsPage = lazy(() => import("@/pages/results/ResultsPage"));
const ResultDetailPage = lazy(() => import("@/pages/results/ResultDetailPage"));
const SecureResultLinkPage = lazy(
  () => import("@/pages/results/SecureResultLinkPage"),
);
const ResultAccessGate = lazy(
  () => import("@/components/ResultAccessGate"),
);
const NotificationsPage = lazy(
  () => import("@/pages/notifications/NotificationsPage"),
);
const PreferencesPage = lazy(
  () => import("@/pages/preferences/PreferencesPage"),
);

const LoginPage = lazy(() => import("@/pages/auth/LoginPage"));
const ForgotPasswordPage = lazy(
  () => import("@/pages/auth/ForgotPasswordPage"),
);
const ResetPasswordPage = lazy(
  () => import("@/pages/auth/ResetPasswordPage"),
);
const VerifyEmailPage = lazy(() => import("@/pages/auth/VerifyEmailPage"));
const NotFoundPage = lazy(() => import("@/pages/NotFoundPage"));

function Lazy({ children }: { children: React.ReactNode }) {
  return <Suspense fallback={<LoadingPage />}>{children}</Suspense>;
}

function RootLayout() {
  return (
    <Providers>
      <Outlet />
    </Providers>
  );
}

export const router = createBrowserRouter([
  {
    element: <RootLayout />,
    errorElement: <ApplicationLoadError />,
    children: [
      {
        element: (
          <Lazy>
            <PortalLayout />
          </Lazy>
        ),
        errorElement: <ErrorPage />,
        children: [
          {
            index: true,
            element: (
              <Lazy>
                <DashboardPage />
              </Lazy>
            ),
          },
          {
            path: "orders",
            element: (
              <PortalCapabilityRoute capability="order">
                <Lazy>
                  <OrdersPage />
                </Lazy>
              </PortalCapabilityRoute>
            ),
          },
          {
            path: "orders/new",
            element: (
              <PortalCapabilityRoute capability="order">
                <Lazy>
                  <NewOrderPage />
                </Lazy>
              </PortalCapabilityRoute>
            ),
          },
          {
            path: "orders/:id",
            element: (
              <PortalCapabilityRoute capability="order">
                <Lazy>
                  <OrderDetailPage />
                </Lazy>
              </PortalCapabilityRoute>
            ),
          },
          {
            element: (
              <PortalCapabilityRoute capability="viewResults">
                <Lazy>
                  <ResultAccessGate />
                </Lazy>
              </PortalCapabilityRoute>
            ),
            children: [
              {
                path: "results",
                element: (
                  <Lazy>
                    <ResultsPage />
                  </Lazy>
                ),
              },
              {
                path: "results/:orderId",
                element: (
                  <Lazy>
                    <ResultDetailPage />
                  </Lazy>
                ),
              },
            ],
          },
          {
            path: "notifications",
            element: (
              <Lazy>
                <NotificationsPage />
              </Lazy>
            ),
          },
          {
            path: "preferences",
            element: (
              <Lazy>
                <PreferencesPage />
              </Lazy>
            ),
          },
        ],
      },
      {
        element: (
          <Lazy>
            <AuthLayout />
          </Lazy>
        ),
        children: [
          {
            path: "login",
            element: (
              <Lazy>
                <LoginPage />
              </Lazy>
            ),
          },
          {
            path: "forgot-password",
            element: (
              <Lazy>
                <ForgotPasswordPage />
              </Lazy>
            ),
          },
          {
            path: "reset-password",
            element: (
              <Lazy>
                <ResetPasswordPage />
              </Lazy>
            ),
          },
          {
            path: "verify-email",
            element: (
              <Lazy>
                <VerifyEmailPage />
              </Lazy>
            ),
          },
          {
            path: "result-link/:linkId",
            element: (
              <Lazy>
                <SecureResultLinkPage />
              </Lazy>
            ),
          },
        ],
      },
      {
        path: "*",
        element: (
          <Lazy>
            <NotFoundPage />
          </Lazy>
        ),
      },
    ],
  },
]);
